import {Group} from 'three';
import TWEEN from '@tweenjs/tween.js';
import {
  LITTLE_MAN_WIDTH,
  LITTLE_MAN_HEIGHT,
  JUMP_TIME,
  HIGH_JUMP,
  STORAGE_TIME,
  ENABLE_AUTO_JUMP
} from "../config/constant";
import {animateFrame} from '../util/TweenUtil';
import Box from './Box';
import {chargeDistance, landingOn} from '../util/Gameplay';
import Particle from './Particle';
import Tail from './Tail';
import YellowCharacter from './YellowCharacter';

class LittleMan {

  constructor (stage, boxGroup, game) {
    this.stage = stage;
    this.game = game;
    this.boxGroup = boxGroup;
    // 身体、表情与四肢一起压缩，保持软乎乎的完整轮廓。
    this.trunk = null;
    // 头部 + 躯干
    this.body = null;
    // 控制旋转的部分
    // 和 body 的区别在于坐标基点不同，也就导致旋转的中心不同
    this.bodyRotate = null;
    // 站在盒子上
    this.box = null;
    // 默认状态
    this.state = LittleMan.STATE.init;

    // 残影
    this.tail = null;

    // 粒子
    this.particle = new Particle();

    this.init();
  }

  init () {
    // 创建躯干
    this.initTrunk();
    // 整体与翻转中心
    this.initBody();
    // 初始化位置
    this.initPosition();
    // 初始化拖尾
    this.initTail();
    // 初始化事件监听
    this.tail.markPosition();
  }

  // 躯干
  initTrunk() {
    this.trunk = new YellowCharacter(LITTLE_MAN_HEIGHT, () => this.stage.render());
  }

  // 身体
  initBody() {
    // bodyRotate 将旋转中心点移动到物体的中间部分
    this.bodyRotate = new Group();
    this.bodyRotate.translateY(LITTLE_MAN_HEIGHT/2);
    this.bodyRotate.add(this.trunk);
    this.trunk.translateY(-LITTLE_MAN_HEIGHT/2);

    // 注意，body 的 position 还是在坐标原点，也就是小人的脚下
    this.body = new Group();
    this.body.add(this.bodyRotate);
  }

  // 初始化小人的高度
  initPosition() {
    this.box = this.boxGroup.last.prev;

    // 向上移动盒子的高度
    this.body.translateY(this.box.height);
    this.faceNextPlatform();
  }

  // 尾巴展示跳跃过程中的残影
  initTail() {
    this.tail = new Tail(this.stage.scene, this.bodyRotate);
  }

  beginCharge() {
    if (this.state !== LittleMan.STATE.init || this.game.state !== 'playing') return;
    this.faceNextPlatform();
    this.state = LittleMan.STATE.storage;
    this.chargeStart = performance.now();
    this.chargeProgress = 0;
    this.particle.gather(this.body);
    this.tail.markPosition();
    this.game.sound.play('charge');
    this.chargeTween = new TWEEN.Tween({t: 0}).to({t: 1}, STORAGE_TIME)
      .onUpdate(({t}) => {
        this.chargeProgress = t;
        this.applyCompression(Math.pow(t, 1.35));
        this.game.setCharge(t);
      }).start();
    animateFrame();
  }
  applyCompression(t) {
    this.trunk.scale.set(1 + t * 0.18, 1 - t * 0.3, 1 + t * 0.18);
    this.box.scaleY(1 - t * 0.2);
    this.body.position.y = this.box.height;
  }
  cancelCharge() {
    this.game.sound.stopCharge?.();
    if (this.state !== LittleMan.STATE.storage) return;
    this.chargeTween.stop(); this.particle.stopGather();
    this.applyCompression(0); this.game.setCharge(0);
    this.state = LittleMan.STATE.init;
  }
  releaseCharge() {
    if (this.state !== LittleMan.STATE.storage || this.game.state !== 'playing') return;
    this.game.sound.stopCharge?.();
    this.chargeTween.stop(); this.particle.stopGather();
    // Use elapsed time so short presses and slow displays produce the same distance.
    const progress = Math.min(1, (performance.now() - this.chargeStart) / STORAGE_TIME);
    this.game.setCharge(0);
    this.jump(chargeDistance(progress));
  }
  jump(distance) {
    if (this.box.cancelStay) this.box.cancelStay();
    this.state = LittleMan.STATE.jumping;
    const next = this.box.next;
    const start = this.body.position.clone();
    const dx = next.position.x - start.x, dz = next.position.z - start.z;
    const length = Math.hypot(dx, dz);
    this.trunk.faceTowards(dx, dz);
    if (ENABLE_AUTO_JUMP) distance = length;
    const target = {x: start.x + dx / length * distance, z: start.z + dz / length * distance};
    this.applyCompression(0);
    this.trunk.setAirborne(true);
    const footY = Box.defaultHeight;
    const height = HIGH_JUMP * (0.65 + 0.35 * distance / 112);
    this.game.sound.play('jump');
    this.tail.markPosition();
    let previous = 0;
    new TWEEN.Tween({t: 0}).to({t: 1}, JUMP_TIME)
      .onUpdate(({t}) => {
        this.body.position.set(start.x + (target.x - start.x) * t,
          footY + 4 * height * t * (1 - t), start.z + (target.z - start.z) * t);
        this.trunk.setJumpRotation(-2 * Math.PI * t);
        this.tail.showTail((t - previous) * JUMP_TIME); previous = t;
      })
      .onComplete(() => {
        this.trunk.setJumpRotation(0); this.tail.reset(); this.afterJump(target);
      }).start();
    animateFrame();
  }
  afterJump(point) {
    const next = this.box.next;
    const nextResult = landingOn(next, point, LITTLE_MAN_WIDTH / 2);
    const currentResult = landingOn(this.box, point, LITTLE_MAN_WIDTH / 2);
    if (nextResult === 'normal' || nextResult === 'perfect') {
      this.box = next;
      this.game.onLanding(nextResult);
      this.box.doAnimate(this.game);
      this.boxGroup.createBox();
      this.landingFeedback(() => this.boxGroup.updatePosition({
        duration: 420, onComplete: () => { this.faceNextPlatform(); this.state = LittleMan.STATE.init; }
      }));
    } else if (currentResult === 'normal' || currentResult === 'perfect') {
      this.game.onLanding('stay');
      if (this.box.cancelStay) this.box.doAnimate(this.game);
      this.landingFeedback(() => { this.faceNextPlatform(); this.state = LittleMan.STATE.init; });
    } else {
      this.fail(nextResult === 'edge' || currentResult === 'edge');
    }
  }
  faceNextPlatform() {
    const next = this.box.next;
    if (next) this.trunk.faceTowards(next.position.x - this.body.position.x, next.position.z - this.body.position.z);
  }
  landingFeedback(done) {
    this.trunk.setAirborne(false);
    this.particle.scatter();
    this.stage.shake();
    new TWEEN.Tween({t: 0}).to({t: 1}, 180).onUpdate(({t}) => {
      const amount = Math.sin(Math.PI * t) * 0.14;
      this.trunk.scale.set(1 + amount, 1 - amount, 1 + amount);
    }).onComplete(() => {
      this.trunk.scale.set(1, 1, 1); done();
    }).start();
    animateFrame();
  }
  fail(edge) {
    this.trunk.setAirborne(true);
    this.state = LittleMan.STATE.falling;
    this.game.onFailStart();
    const y = this.body.position.y;
    const dx = this.box.next.position.x - this.box.position.x;
    const dz = this.box.next.position.z - this.box.position.z;
    const length = Math.hypot(dx, dz);
    const start = this.body.position.clone();
    new TWEEN.Tween({t: 0}).to({t: 1}, edge ? 600 : 420)
      .easing(TWEEN.Easing.Quadratic.In).onUpdate(({t}) => {
        this.body.position.y = y * (1 - t);
        if (edge) {
          this.body.position.x = start.x + dx / length * LITTLE_MAN_WIDTH * t;
          this.body.position.z = start.z + dz / length * LITTLE_MAN_WIDTH * t;
          this.trunk.setJumpRotation(-Math.PI / 2 * t);
        }
      }).onComplete(() => this.game.finish()).start();
    animateFrame();
  }
  enterStage(stage) { stage.scene.add(this.body); }
}
LittleMan.STATE = {init: 1, storage: 2, jumping: 3, falling: 4};
export default LittleMan;
